import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PageHeader from '../../components/PageHeader/PageHeader';
import { ApplicationSummaryCard, MonitoringCard, StagesList } from './components';
import useBrandRegistrationStagesService, { StagesData, SummaryData, Stage } from '../../app/services/hooks/useBrandRegistrationStagesService';
import { toast } from 'react-toastify';

interface RouteParams {
    id: string;
}

const BrandRegistrationStages: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();
    const { getBrandRegistrationStages, fetchingGetBrandRegistrationStages } = useBrandRegistrationStagesService();

    const [stagesData, setStagesData] = useState<StagesData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (id) {
            loadStages();
        }
    }, [id]);

    const loadStages = () => {
        if (!id) return;

        getBrandRegistrationStages(
            parseInt(id),
            {
                onSuccess: (response) => {
                    if (response.data) {
                        setStagesData(response.data);
                    } else {
                        toast.error(response.message || 'Error al cargar las etapas');
                    }
                    setLoading(false);
                },
                onError: (response) => {
                    toast.error(response.message || 'Error al cargar las etapas');
                    setLoading(false);
                }
            }
        );
    };

    const handleStageClick = (stagePath: string) => {
        history.push(stagePath);
    };

    // Mostrar loading mientras se cargan los datos
    if (loading || fetchingGetBrandRegistrationStages) {
        return (
            <div className="container-fluid">
                <div className="page-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Si no hay datos, mostrar error
    if (!stagesData) {
        return (
            <div className="container-fluid">
                <div className="page-container">
                    <div className="alert alert-danger" role="alert">
                        No se pudieron cargar los datos de las etapas.
                    </div>
                </div>
            </div>
        );
    }

    // Transformar las etapas para incluir las rutas
    const stagesWithPaths: Array<{
        id: number;
        title: string;
        description: string;
        status: 'completed' | 'pending' | 'current';
        path: string;
    }> = stagesData.stages.map(stage => ({
        ...stage,
        path: `/intranet/brand-registration/edit/${id}/step/${stage.stage_number}`
    }));

    // Transformar los datos del resumen
    const applicationData = {
        id: stagesData.summary.id.toString(),
        serviceType: stagesData.summary.service_type,
        assignedLawyer: stagesData.summary.assigned_lawyer,
        status: stagesData.summary.status
    };

    return (
        <div className="container-fluid">
            <div className="page-container">
                <PageHeader
                    breadcrumbs={[
                        { label: 'Inicio', path: '/intranet' },
                        { label: 'Registro de marca', path: '/intranet/brand-registration' },
                        { label: stagesData.summary.brand_name || 'Etapas', isCurrent: true }
                    ]}
                    title={stagesData.summary.brand_name ? `${stagesData.summary.brand_name}: etapas del registro` : 'Etapas del registro'}
                    description={stagesData.summary.is_completed
                        ? 'El registro completó todas sus etapas: la marca está registrada.'
                        : 'El registro avanza etapa por etapa, desde la factibilidad hasta el certificado de la marca.'}
                />

                <ApplicationSummaryCard data={applicationData} />

                {stagesData.summary.is_completed && (
                    <MonitoringCard
                        brandRegistrationId={stagesData.summary.id}
                        enabled={!!stagesData.summary.monitoring_enabled}
                        canManage={!!stagesData.summary.can_manage_monitoring}
                        pendingAlerts={stagesData.summary.pending_alerts || 0}
                        onChange={(enabled) => setStagesData({
                            ...stagesData,
                            summary: { ...stagesData.summary, monitoring_enabled: enabled }
                        })}
                    />
                )}

                {stagesData.summary.payment_managed_by_firm && (
                    <div className="alert alert-info d-flex align-items-center gap-2" data-testid="payment-managed-by-firm">
                        <i className="fas fa-handshake" />
                        <span>
                            {stagesData.summary.is_applicant
                                ? 'El pago de este trámite lo acuerdas directamente con tu estudio.'
                                : 'Pago acordado internamente con el cliente.'}
                        </span>
                    </div>
                )}
                
                <StagesList 
                    stages={stagesWithPaths} 
                    onStageClick={handleStageClick}
                    verificationId={stagesData.summary.verification_id}
                    verificationPaymentStatus={stagesData.summary.verification_payment_status}
                    isApplicant={stagesData.summary.is_applicant}
                    paymentUrl={stagesData.summary.payment_url}
                    paymentManagedByFirm={!!stagesData.summary.payment_managed_by_firm}
                />
            </div>
        </div>
    );
};

export default BrandRegistrationStages;